/*
 * Security profiles (docs/api.md, "Security profiles"; design in
 * docs/security-controls.md): named sets of security controls that
 * environments follow, live-linked like saved address lists. Standard,
 * Recommended and Strict are built in and can't be changed; an org makes its
 * own from one of them or another profile. One is the default for new
 * environments (Recommended until the org picks one).
 *
 * An environment follows one profile; a control set differently on it is an
 * override ("Strict, changed here"). Changing a profile changes every
 * environment following it, except the controls they override. Deleting one
 * moves its environments to its base, keeping what they run.
 *
 * Owners and admins manage profiles (`canManage`, and each profile's
 * `editable`); an environment's profile takes manage on that environment.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrg } from "../app/org";
import { api } from "./client";
import { keys } from "./keys";
import { watchOperation } from "./live";
import { ctlKey, notYet, type ProfileControls, type SecurityControls } from "./securityControls";

export type { ProfileControls };

/** One control in words, for chips: "Login limit" "10/min". */
export type ProfileChip = { key: string; label: string; value: string; tone: "good" | "warn" | "off" };

export type Affected = { environments: number; sites: number };

export type SecurityProfile = {
  /** "standard", "recommended", "strict", or a uuid. */
  id: string;
  name: string;
  builtIn: boolean;
  /** The org that made it; null when built in. */
  orgId: string | null;
  /** Whether the caller may change it here. */
  editable: boolean;
  /** Where its environments go if it's deleted. */
  base: { id: string; name: string } | null;
  controls: ProfileControls;
  summary: ProfileChip[];
  /** Following it, in this org and the orgs below. */
  usage: Affected;
  isDefault: boolean;
  updatedAt: string | null;
  updatedBy: { id: string; name: string } | null;
};

export type ProfileFollower = { environmentId: string; siteId: string; siteName: string; environment: string; label: string; overrides: string[] };

export type ProfileList = { profiles: SecurityProfile[]; defaultProfileId: string | null; defaultInheritedFrom: { orgId: string; orgName: string } | null; canManage: boolean };

/** Any part of the controls, any of its fields. */
export type ControlsPatch = { [K in keyof ProfileControls]?: Partial<ProfileControls[K]> };

export type ProfileChange = { profile: SecurityProfile; affected: Affected; followers: number; changed: string[]; dryRun: boolean };

const base = (orgId: string) => `/orgs/${orgId}/security-profiles`;
const profilesKey = (orgId: string) => ["org", orgId, "security-profiles"] as const;
const retry = (n: number, e: unknown) => !notYet(e) && n < 2;

/** Every profile the org can use, in the API's order: Standard, Recommended, Strict, the org's own, then those of the orgs above. */
export function useSecurityProfiles(enabled = true) {
  const o = useOrg();
  return useQuery({
    queryKey: profilesKey(o.id),
    queryFn: ({ signal }) => api.get<ProfileList>(base(o.id), signal),
    enabled,
    retry,
    staleTime: 15_000,
  });
}

/** One profile, with the environments following it that the caller can see. */
export function useSecurityProfile(id: string | null) {
  const o = useOrg();
  return useQuery({
    queryKey: [...profilesKey(o.id), "one", id],
    queryFn: ({ signal }) => api.get<{ profile: SecurityProfile & { environments: ProfileFollower[] } }>(`${base(o.id)}/${id}`, signal),
    select: (d) => d.profile,
    enabled: !!id,
    retry,
  });
}

/** After a profile changes: the list, the profile, and every site's security (environments follow profiles). */
function useInvalidateProfiles() {
  const o = useOrg();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: profilesKey(o.id) });
    void qc.invalidateQueries({ queryKey: ["org", o.id, "site"] });
    void qc.invalidateQueries({ queryKey: keys.operationLists(o.id) });
  };
}

/** A new profile, a copy of `from` (Standard when left out), which is also its base. */
export function useCreateProfile() {
  const o = useOrg();
  const done = useInvalidateProfiles();
  return useMutation({
    mutationFn: (v: { name: string; from?: string }) => api.post<{ profile: SecurityProfile }>(base(o.id), v).then((r) => r.profile),
    onSuccess: done,
  });
}

/** Renames or changes a profile. Built-ins answer 409. */
export function useUpdateProfile() {
  const o = useOrg();
  const done = useInvalidateProfiles();
  return useMutation({
    mutationFn: (v: { id: string; name?: string; controls?: ControlsPatch }) => api.patch<ProfileChange>(`${base(o.id)}/${v.id}`, { name: v.name, controls: v.controls }),
    onSuccess: done,
  });
}

/** What saving would reach, without saving ("Updates 12 environments on 7 sites"). */
export function useProfileDryRun(id: string, patch: { name?: string; controls?: ControlsPatch }, enabled: boolean) {
  const o = useOrg();
  return useQuery({
    queryKey: [...profilesKey(o.id), "dry-run", id, patch],
    queryFn: () => api.patch<ProfileChange>(`${base(o.id)}/${id}`, { ...patch, dryRun: true }),
    enabled,
    retry: false,
    staleTime: 0,
    gcTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export type ProfileDeletion = { ok: boolean; dryRun: boolean; affected: Affected; moveTo: { id: string; name: string } };

/** Where a profile's environments would go, for the confirm. */
export function useDeleteDryRun(id: string | null) {
  const o = useOrg();
  return useQuery({
    queryKey: [...profilesKey(o.id), "delete-dry-run", id],
    queryFn: () => api.del<ProfileDeletion>(`${base(o.id)}/${id}`, { dryRun: true }),
    enabled: !!id,
    retry: false,
    staleTime: 0,
  });
}

/** Deletes a profile: its environments follow its base, keeping what they run. */
export function useDeleteProfile() {
  const o = useOrg();
  const done = useInvalidateProfiles();
  return useMutation({
    mutationFn: (id: string) => api.del<ProfileDeletion>(`${base(o.id)}/${id}`, {}),
    onSuccess: done,
  });
}

/** The profile new environments follow. Answers with the list. */
export function useSetDefaultProfile() {
  const o = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (profileId: string) => api.put<ProfileList>(`${base(o.id)}/default`, { profileId }),
    onSuccess: (r) => qc.setQueryData(profilesKey(o.id), r),
  });
}

/* ── An environment's profile ───────────────────────────────────── */

const envBase = (o: string, s: string, e: string) => `/orgs/${o}/sites/${s}/environments/${e}/security`;

function useEnvWrite<V>(siteId: string, envId: string, fn: (orgId: string, v: V) => Promise<{ controls: SecurityControls; operationId: string | null }>, title: string) {
  const o = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: V) => fn(o.id, v),
    onSuccess: (r) => {
      qc.setQueryData(ctlKey(o.id, siteId, envId), r.controls);
      if (r.operationId) watchOperation(r.operationId, { title, description: "The new rules are live." });
      void qc.invalidateQueries({ queryKey: keys.site(o.id, siteId) });
      void qc.invalidateQueries({ queryKey: profilesKey(o.id) });
      void qc.invalidateQueries({ queryKey: keys.operationLists(o.id) });
    },
  });
}

/** Moves an environment to another profile; its own changes are dropped unless kept. */
export const useSetEnvProfile = (siteId: string, envId: string) =>
  useEnvWrite(siteId, envId, (orgId, v: { profileId: string; keepOverrides: boolean }) => api.put(`${envBase(orgId, siteId, envId)}/profile`, v), "Security profile applied");

/** "Reset to profile": every control back to the profile's. */
export const useResetOverrides = (siteId: string, envId: string) =>
  useEnvWrite(siteId, envId, (orgId, _v: void) => api.del(`${envBase(orgId, siteId, envId)}/overrides`, {}), "Reset to the security profile");

/* ── Words ──────────────────────────────────────────────────────── */

/** "7 sites"; "12 environments on 7 sites". */
export const usedWords = (u: Affected) => `${u.sites} ${u.sites === 1 ? "site" : "sites"}`;
export const affectedWords = (u: Affected) =>
  `${u.environments} ${u.environments === 1 ? "environment" : "environments"} on ${u.sites} ${u.sites === 1 ? "site" : "sites"}`;

/** "firewall and login limit", from an environment's overrides in words (an acronym like HSTS stays as it is). */
export function overrideList(changes: { label: string }[]): string {
  const labels = changes.map((c) => (/^[A-Z][a-z]/.test(c.label) && !c.label.startsWith("WordPress") ? c.label.charAt(0).toLowerCase() + c.label.slice(1) : c.label));
  return labels.length <= 1 ? (labels[0] ?? "") : `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)}`;
}
